import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PaperPlaneTilt, Lightning, WarningCircle, Paperclip, CircleNotch, X } from "@phosphor-icons/react";

function countBodyParams(components) {
  const body = (components || []).find((c) => c.type === "BODY");
  if (!body) return 0;
  const txt = body.text || "";
  const matches = txt.match(/{{\d+}}/g) || [];
  return matches.length;
}

function countHeaderTextParams(components) {
  const header = (components || []).find((c) => c.type === "HEADER");
  if (!header || header.format !== "TEXT") return 0;
  const matches = (header.text || "").match(/{{\d+}}/g) || [];
  return matches.length;
}

// Meta templates can have URL buttons whose `url` field itself contains a
// `{{1}}` variable that must be supplied at send time. We detect each such
// button so the sender can fill in the final URL segment.
function extractUrlButtonVars(components) {
  const btnComp = (components || []).find((c) => c.type === "BUTTONS");
  if (!btnComp) return [];
  const out = [];
  (btnComp.buttons || []).forEach((b, idx) => {
    if (b.type !== "URL") return;
    const raw = b.url || "";
    if (!raw.includes("{{")) return;
    out.push({
      index: idx,
      text: b.text || `Button ${idx + 1}`,
      url_template: raw,
      example: (b.example && b.example[0]) || "",
    });
  });
  return out;
}

// Detect if the template's HEADER is a media format (IMAGE / VIDEO /
// DOCUMENT). Returns the lowercase kind ("image"|"video"|"document") or
// null when the header is text-only / absent.
function headerMediaKind(components) {
  const header = (components || []).find((c) => c.type === "HEADER");
  if (!header) return null;
  const fmt = (header.format || "").toUpperCase();
  if (fmt === "IMAGE") return "image";
  if (fmt === "VIDEO") return "video";
  if (fmt === "DOCUMENT") return "document";
  return null;
}

export default function NewChatDialog({ open, onClose }) {
  const [phone, setPhone] = useState("");
  const [templates, setTemplates] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [selected, setSelected] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [params, setParams] = useState([]);
  const [headerParams, setHeaderParams] = useState([]);
  const [buttonUrlParams, setButtonUrlParams] = useState([]); // [{index, param}]
  const [sending, setSending] = useState(false);
  // Media header (IMAGE/VIDEO/DOCUMENT) — uploaded to Meta first, then
  // the returned media_id is attached to the /chat/initiate payload.
  const [headerMedia, setHeaderMedia] = useState(null); // {id, kind, filename, previewUrl}
  const [uploadingMedia, setUploadingMedia] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/whatsapp/templates").then((r) => {
      setTemplates(r.data.templates || []);
      setConfigured(r.data.configured !== false);
    }).catch(() => setConfigured(false));
  }, [open]);

  const tpl = templates.find((t) => `${t.name}:${t.language}` === selected);
  const needed = tpl ? countBodyParams(tpl.components) : 0;
  const headerNeeded = tpl ? countHeaderTextParams(tpl.components) : 0;
  const urlButtonVars = tpl ? extractUrlButtonVars(tpl.components) : [];
  const mediaKind = tpl ? headerMediaKind(tpl.components) : null;

  useEffect(() => {
    setParams(Array(needed).fill(""));
    setHeaderParams(Array(headerNeeded).fill(""));
    setButtonUrlParams(urlButtonVars.map((b) => ({ index: b.index, param: "" })));
    setHeaderMedia(null);
    if (tpl) setLanguage(tpl.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, needed, headerNeeded, urlButtonVars.length]);

  // Upload the chosen file straight to Meta via /api/media/upload. We
  // store the returned media_id locally; on submit it's sent inside
  // `header_media` so the backend can build the IMAGE/VIDEO/DOCUMENT
  // header parameter.
  const uploadHeaderMedia = async (file) => {
    if (!file || !mediaKind) return;
    const accept = {
      image: ["image/jpeg", "image/png", "image/webp"],
      video: ["video/mp4", "video/3gp", "video/quicktime"],
      document: null,  // Meta accepts most doc types — let backend reject
    }[mediaKind];
    if (accept && !accept.includes(file.type)) {
      toast.error(`This template needs ${mediaKind.toUpperCase()} — selected file is ${file.type || "unknown"}.`);
      return;
    }
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("wh_token");
      const up = await fetch(`${API_BASE}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!up.ok) {
        const e = await up.json().catch(() => ({}));
        throw new Error(e?.detail?.message || e?.message || "Upload failed");
      }
      const { media_id } = await up.json();
      setHeaderMedia({
        id: media_id,
        kind: mediaKind,
        filename: file.name,
        previewUrl: mediaKind === "image" ? URL.createObjectURL(file) : null,
      });
      toast.success(`${mediaKind} uploaded`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploadingMedia(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!tpl) return toast.error("Select a template");
    if (!phone.trim()) return toast.error("Enter phone number");
    if (mediaKind && !headerMedia?.id) {
      return toast.error(`Please upload the ${mediaKind} for this template's header.`);
    }
    setSending(true);
    try {
      await api.post("/chat/initiate", {
        to: phone.replace(/\D/g, ""),
        template_name: tpl.name,
        language: tpl.language,
        body_params: params,
        header_params: headerParams,
        button_url_params: buttonUrlParams.filter((b) => b.param),
        header_media: headerMedia
          ? { id: headerMedia.id, kind: headerMedia.kind, filename: headerMedia.filename }
          : null,
      });
      toast.success("Template sent!");
      setPhone("");
      setSelected("");
      setParams([]);
      setHeaderParams([]);
      setButtonUrlParams([]);
      setHeaderMedia(null);
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-chat-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Start a new conversation</DialogTitle>
          <DialogDescription>
            Cold outreach requires an approved Meta template (24h rule).
          </DialogDescription>
        </DialogHeader>

        {!configured && (
          <div className="rounded-lg bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] p-3 text-xs flex gap-2">
            <WarningCircle size={16} className="shrink-0 mt-0.5" />
            <div>
              <b>WHATSAPP_WABA_ID not set.</b> Add it in backend .env / Secrets to
              fetch Meta-approved templates.
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Phone number (with country code, no +)</Label>
            <Input
              required
              placeholder="919876543210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="new-chat-phone"
              inputMode="numeric"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Template ({templates.length} approved)</Label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-[var(--border)] bg-[var(--surface)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--fg)]/20"
              data-testid="new-chat-template"
            >
              <option value="">
                {templates.length ? "Select a template" : "No templates available"}
              </option>
              {templates.map((t) => (
                <option key={`${t.name}:${t.language}`} value={`${t.name}:${t.language}`}>
                  {t.name} · {t.language} · {t.category}
                </option>
              ))}
            </select>
          </div>

          {tpl && (
            <div className="rounded-md bg-[var(--bg)] border border-[var(--border)] p-3 text-xs whitespace-pre-wrap">
              {(tpl.components || [])
                .filter((c) => ["HEADER", "BODY", "FOOTER"].includes(c.type))
                .map((c) => c.text || (c.format ? `[${c.format}]` : ""))
                .filter(Boolean)
                .join("\n\n")}
            </div>
          )}

          {/* Media-header uploader — visible only when the selected
              template has an IMAGE / VIDEO / DOCUMENT header. The agent
              MUST upload the asset; Meta rejects the send otherwise. */}
          {mediaKind && (
            <div
              className="space-y-1.5 rounded-md border border-[#25D366]/40 bg-[#25D366]/5 p-2.5"
              data-testid="header-media-uploader"
            >
              <Label className="text-xs flex items-center gap-1.5 text-[#15803D] dark:text-[#86EFAC] font-semibold">
                <Paperclip size={14} weight="bold" />
                Header {mediaKind} <span className="opacity-70 font-normal">(required)</span>
              </Label>
              {!headerMedia ? (
                <label className="flex items-center justify-center gap-2 h-20 rounded-md border-2 border-dashed border-[#25D366]/40 cursor-pointer hover:bg-[#25D366]/10 text-xs text-[var(--muted)]">
                  <input
                    type="file"
                    className="hidden"
                    disabled={uploadingMedia}
                    accept={
                      mediaKind === "image"
                        ? "image/jpeg,image/png,image/webp"
                        : mediaKind === "video"
                          ? "video/mp4,video/3gpp,video/quicktime"
                          : "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    }
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadHeaderMedia(f);
                      e.target.value = "";
                    }}
                    data-testid="header-media-input"
                  />
                  {uploadingMedia ? (
                    <>
                      <CircleNotch size={16} className="animate-spin" /> Uploading…
                    </>
                  ) : (
                    <>
                      <Paperclip size={16} weight="bold" />
                      Click to upload {mediaKind}
                    </>
                  )}
                </label>
              ) : (
                <div className="flex items-center gap-2 p-2 rounded-md bg-[var(--surface)] border border-[var(--border)]">
                  {headerMedia.previewUrl ? (
                    <img
                      src={headerMedia.previewUrl}
                      alt="header preview"
                      className="h-12 w-12 rounded object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded bg-[var(--bg)] flex items-center justify-center shrink-0">
                      <Paperclip size={18} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate" data-testid="header-media-filename">
                      {headerMedia.filename}
                    </div>
                    <div className="text-[10px] text-[var(--muted)]">Uploaded to Meta ✓</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHeaderMedia(null)}
                    className="p-1 rounded hover:bg-[var(--bg)] shrink-0"
                    title="Remove"
                    data-testid="header-media-remove"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              )}
            </div>
          )}

          {headerParams.map((p, i) => (
            <div key={`h-${i}`} className="space-y-1.5">
              <Label className="text-xs">Header variable {`{{${i + 1}}}`}</Label>
              <Input
                required
                placeholder={`Header value for {{${i + 1}}}`}
                value={p}
                onChange={(e) =>
                  setHeaderParams((prev) =>
                    prev.map((v, idx) => (idx === i ? e.target.value : v)),
                  )
                }
              />
            </div>
          ))}

          {params.map((p, i) => (
            <div key={i} className="space-y-1.5">
              <Label className="text-xs">Variable {`{{${i + 1}}}`}</Label>
              <Input
                required
                placeholder={`Value for {{${i + 1}}}`}
                value={p}
                onChange={(e) =>
                  setParams((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))
                }
              />
            </div>
          ))}

          {urlButtonVars.map((btn, i) => {
            const current = buttonUrlParams[i]?.param || "";
            const previewUrl = btn.url_template.replace(/{{\d+}}/g, current || "{{1}}");
            return (
              <div key={`btn-${btn.index}`} className="space-y-1.5 rounded-md border border-[#FDE68A] bg-[#FEF3C7]/40 dark:bg-[#78350F]/20 p-2.5">
                <Label className="text-xs flex items-center gap-1.5 text-[#92400E] dark:text-[#FBBF24] font-semibold">
                  🔗 URL Button: <span className="font-mono">{btn.text}</span>
                </Label>
                <Input
                  required
                  placeholder={btn.example || "e.g. oggg011ro55g7b5p11ebdplzo"}
                  value={current}
                  onChange={(e) =>
                    setButtonUrlParams((prev) =>
                      prev.map((b, idx) => (idx === i ? { ...b, param: e.target.value } : b)),
                    )
                  }
                  data-testid={`btn-url-param-${btn.index}`}
                />
                <div className="text-[10px] text-[var(--muted)] font-mono break-all">
                  <span className="opacity-60">Final URL:</span> {previewUrl}
                </div>
              </div>
            );
          })}

          <DialogFooter>
            <Button type="submit" disabled={sending} className="gap-1.5" data-testid="new-chat-send">
              {sending ? <Lightning size={14} /> : <PaperPlaneTilt size={14} weight="fill" />}
              {sending ? "Sending…" : "Send template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
